import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Move } from 'lucide-react';
import { drawLearningPreviewFrame } from '@/lib/learningTutorial/previewRenderer';
import {
  DESIGN_H,
  DESIGN_W,
  getEditorOverlays,
  sceneAtPlayhead,
  sceneIndexAtTime,
} from '@/lib/learningTutorial/layout';
import { mergeSegmentsIntoRendered } from '@/lib/learningTutorial/mergeRendered';
import { activeNarratorCueAtPlayhead } from '@/lib/learningTutorial/narratorSync';
import { preloadLearningImages } from '@/lib/learningTutorial/imagePreload';
import { resolveLogoPreviewUrl } from '@/lib/learningTutorial/logoBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const TEXT_STYLE = {
  scene_title: 'font-bold text-[28px] text-emerald-400 leading-tight',
  explanation: 'text-[18px] text-slate-100 leading-snug',
  bullet: 'text-[18px] text-slate-100 leading-snug',
  keyword: 'text-sm font-semibold text-amber-300 uppercase',
  console_output: 'font-mono text-[16px] text-green-300 leading-snug',
};

function DraggableOverlay({
  item,
  selected,
  scale,
  onSelect,
  onDragEnd,
  onResizeEnd,
  onTextChange,
  onImageUpload,
}) {
  const dragRef = useRef(null);

  const onPointerDown = (e, mode = 'move') => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(item.id);
    const parent = e.currentTarget.closest('[data-preview-root]');
    const rect = parent?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      orig: { x: item.x, y: item.y, w: item.w, h: item.h },
      rect,
    };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (ev.clientX - d.startX) / scale;
      const dy = (ev.clientY - d.startY) / scale;
      if (d.mode === 'resize') {
        onDragEnd(item.id, {
          x: d.orig.x,
          y: d.orig.y,
          w: Math.max(80, d.orig.w + dx),
          h: Math.max(40, d.orig.h + dy),
        }, true);
      } else {
        onDragEnd(item.id, {
          x: Math.max(0, Math.min(DESIGN_W - 40, d.orig.x + dx)),
          y: Math.max(0, Math.min(DESIGN_H - 24, d.orig.y + dy)),
          w: d.orig.w,
          h: d.orig.h,
        }, true);
      }
    };

    const onUp = (ev) => {
      const d = dragRef.current;
      if (d) {
        const dx = (ev.clientX - d.startX) / scale;
        const dy = (ev.clientY - d.startY) / scale;
        if (d.mode === 'resize') {
          onResizeEnd(item.id, {
            w: Math.max(80, d.orig.w + dx),
            h: Math.max(40, d.orig.h + dy),
          });
        } else {
          onDragEnd(item.id, {
            x: Math.max(0, Math.min(DESIGN_W - 40, d.orig.x + dx)),
            y: Math.max(0, Math.min(DESIGN_H - 24, d.orig.y + dy)),
            w: d.orig.w,
            h: d.orig.h,
          });
        }
      }
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const boxStyle = {
    left: item.x * scale,
    top: item.y * scale,
    width: item.w * scale,
    height: item.h * scale,
    overflow: 'hidden',
  };

  if (item.kind === 'code_panel') {
    return (
      <div
        className={`absolute border-2 border-dashed rounded-lg ${
          selected ? 'border-amber-400 bg-amber-950/20' : 'border-slate-500/60 bg-slate-900/30'
        }`}
        style={boxStyle}
        onPointerDown={(e) => onPointerDown(e, 'move')}
      >
        <div className="absolute -top-6 left-0 text-[10px] text-amber-400 flex items-center gap-1 pointer-events-none">
          <Move className="w-3 h-3" /> Code panel
        </div>
        <pre className="p-2 text-xs font-mono text-slate-400 pointer-events-none overflow-hidden">
          {(item.codeLines || []).join('\n')}
        </pre>
      </div>
    );
  }

  if (item.kind === 'image') {
    return (
      <div
        className={`absolute group ${selected ? 'ring-2 ring-emerald-400' : ''}`}
        style={boxStyle}
        onPointerDown={(e) => onPointerDown(e, 'move')}
      >
        {item.image_url ? (
          <div className="w-full h-full bg-white border border-slate-600/40 rounded overflow-hidden pointer-events-none">
            <img
              src={item.image_url}
              alt=""
              className="w-full h-full object-contain"
              draggable={false}
              onError={(e) => {
                e.currentTarget.remove();
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800/80 border border-slate-600 rounded text-slate-500 text-xs gap-2">
            <ImagePlus className="w-8 h-8" />
            <label className="cursor-pointer text-emerald-400 hover:underline pointer-events-auto">
              Upload image
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onImageUpload(item.id, e.target.files[0])}
              />
            </label>
          </div>
        )}
        <div
          className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 cursor-se-resize rounded-tl"
          onPointerDown={(e) => onPointerDown(e, 'resize')}
        />
      </div>
    );
  }

  return (
    <div
      className={`absolute group ${selected ? 'ring-2 ring-emerald-400' : 'hover:ring-1 hover:ring-slate-500'}`}
      style={{ ...boxStyle, maxWidth: (item.max_width || item.w) * scale }}
      onPointerDown={(e) => onPointerDown(e, 'move')}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        contentEditable
        suppressContentEditableWarning
        className={`outline-none min-h-[1.5em] px-1 ${TEXT_STYLE[item.type] || TEXT_STYLE.explanation}`}
        onBlur={(e) => onTextChange(item.id, e.currentTarget.innerText.replace(/^•\s*/, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
      >
        {item.text}
      </div>
      <div
        className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500/80 cursor-se-resize opacity-0 group-hover:opacity-100"
        onPointerDown={(e) => onPointerDown(e, 'resize')}
      />
    </div>
  );
}

export function LearningPreviewStage({
  rendered,
  segments,
  playhead,
  editorState,
  selectedOverlayId,
  onSelectOverlay,
  onLayoutChange,
  onTextChange,
  onImageUpload,
  showVideo,
  videoSrc,
  videoRef,
  onVideoTimeUpdate,
  playing = false,
  videoMuted = false,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const mergedRendered = useMemo(
    () => mergeSegmentsIntoRendered(rendered, segments),
    [rendered, segments],
  );

  const overlays = useMemo(
    () => getEditorOverlays(mergedRendered, playhead),
    [mergedRendered, playhead],
  );

  const narratorCue = useMemo(
    () => activeNarratorCueAtPlayhead(mergedRendered, playhead),
    [mergedRendered, playhead],
  );

  const sceneIdx = useMemo(() => {
    const scenes = mergedRendered?.scenes;
    const elements = mergedRendered?.elements;
    if (elements?.length && scenes?.length) {
      const sc = sceneAtPlayhead(scenes, playhead, elements, mergedRendered);
      const i = scenes.findIndex((s) => s.id === sc?.id);
      if (i >= 0) return i;
    }
    return sceneIndexAtTime(scenes, playhead);
  }, [mergedRendered, playhead]);

  const updateScale = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setScale(w / DESIGN_W);
  }, []);

  useEffect(() => {
    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateScale]);

  const editMode = !showVideo || !videoSrc;
  const canvasPlayback = playing && Boolean(mergedRendered?.elements?.length);
  const showHtmlOverlays = editMode && !playing;

  const [imageCache, setImageCache] = useState({});

  useEffect(() => {
    const url = editorState?.logo?.url;
    if (!url) return undefined;
    const src = resolveLogoPreviewUrl(url, BACKEND_URL);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageCache((prev) => ({ ...prev, __logo__: img, [url]: img }));
    };
    img.src = src;
    return undefined;
  }, [editorState?.logo?.url]);

  useEffect(() => {
    if (!mergedRendered?.elements?.length) return undefined;
    let cancelled = false;
    preloadLearningImages(mergedRendered, BACKEND_URL).then((loaded) => {
      if (!cancelled) {
        setImageCache((prev) => ({ ...prev, ...loaded }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mergedRendered]);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mergedRendered) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const animateOnCanvas = playing || !editMode;
    drawLearningPreviewFrame(ctx, mergedRendered, playhead, editorState, {
      omitEditableLayers: !animateOnCanvas,
      imageCache,
    });
  }, [mergedRendered, playhead, editorState, playing, editMode, imageCache]);

  useEffect(() => {
    paint();
  }, [paint]);

  const handleDrag = (id, patch, isLive) => {
    if (id === '__code_panel__') {
      onLayoutChange({ sceneIndex: sceneIdx, codePanel: patch }, isLive);
      return;
    }
    onLayoutChange({ sceneIndex: sceneIdx, blockId: id, layout: patch }, isLive);
  };

  const handleResize = (id, patch) => {
    handleDrag(id, patch, false);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black rounded-lg overflow-hidden"
      onClick={() => onSelectOverlay(null)}
    >
      {videoSrc ? (
        <video
          ref={videoRef}
          src={videoSrc}
          className={
            showVideo
              ? 'absolute inset-0 w-full h-full object-contain z-0'
              : 'hidden'
          }
          playsInline
          preload="auto"
          muted={videoMuted}
          onTimeUpdate={onVideoTimeUpdate}
        />
      ) : null}
      <canvas
        ref={canvasRef}
        width={DESIGN_W}
        height={DESIGN_H}
        className={`absolute top-0 left-0 ${
          canvasPlayback ? 'opacity-100 z-[3] pointer-events-none' : editMode ? 'opacity-100 z-[1]' : 'opacity-0 pointer-events-none z-[1]'
        }`}
        style={{ width: DESIGN_W * scale, height: DESIGN_H * scale }}
      />
      {showHtmlOverlays && (
        <div
          data-preview-root
          className="absolute top-0 left-0 z-[2]"
          style={{ width: DESIGN_W * scale, height: DESIGN_H * scale }}
        >
          {overlays.map((item) => (
            <DraggableOverlay
              key={item.id}
              item={item}
              selected={selectedOverlayId === item.id}
              scale={scale}
              onSelect={onSelectOverlay}
              onDragEnd={handleDrag}
              onResizeEnd={handleResize}
              onTextChange={onTextChange}
              onImageUpload={onImageUpload}
            />
          ))}
        </div>
      )}
      {narratorCue?.concise_text ? (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[4] max-w-[90%] text-center pointer-events-none">
          <div className="inline-block px-4 py-2 rounded-lg bg-slate-950/85 border border-amber-500/30 shadow-lg">
            <p className="text-[15px] text-amber-50 leading-snug font-medium">
              {narratorCue.concise_text}
            </p>
            {narratorCue.artifact_type ? (
              <p className="text-[10px] text-amber-400/80 mt-1 uppercase tracking-wide">
                {narratorCue.artifact_type}
                {narratorCue.step_index != null ? ` · step ${narratorCue.step_index + 1}` : ''}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="absolute bottom-2 left-2 z-[3] text-[10px] text-slate-400 bg-black/60 px-2 py-0.5 rounded">
        Drag to move · Edit text inline · Resize corner handle
      </div>
    </div>
  );
}

export default LearningPreviewStage;
